import { useEffect, useRef } from "react";
import { createTapfiliateClick } from "@nextday/attribution";
import type { ResolvedApplication } from "@src/funnel/useApplicationContext";

/**
 * Fires a Tapfiliate click creation request once the application context
 * is ready and the app has a configured `tapfiliateAccountId`. The returned
 * visitor ID (`tap_vid`) is persisted in the attribution envelope so it's
 * available when the user is created.
 *
 * Runs at most once per page load (guarded by a ref).
 * Accepts `null` while the context is still loading — no-op in that case.
 */
export const useTapfiliateClick = (application: ResolvedApplication | null): void => {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    if (!application?.tapfiliateAccountId) return;
    fired.current = true;

    createTapfiliateClick({
      tapfiliateAccountId: application.tapfiliateAccountId,
    }).catch(() => {
      // Swallow — attribution is telemetry, never a blocker.
    });
  }, [application?.tapfiliateAccountId]);
};
